import { ElementRef, Injectable, OnDestroy } from '@angular/core';
import { Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, tap } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { WarpProcessRequest } from '../DTO/warpProcessRequest';
import { OAuthService } from 'angular-oauth2-oidc';
import { TranslateService } from "@ngx-translate/core";
import { ClientAccountService } from "./client-account.service";
import { CustomerConverter } from "../converters/customer.converter";
import { AccountConverter } from "../converters/account.converter";
import { ConfigurationService } from '../../shared/services/configuration.service';
import { ProcessStartedModel } from '@app/orders/models/process-started.model';
import { Management } from '@shared/DTO/management';
import { ProcessType } from '@shared/DTO/processType';

const bcompRemoteUrlConfigurations = (bcompSettings) => ({
  'CHAS_REMOTE_URL': bcompSettings.chas.baseUrl,
  'IDEM_REMOTE_URL': bcompSettings.idem.baseUrl,
  'GATTACA_REMOTE_URL': bcompSettings.gattaca.baseUrl,
  'SETI_REMOTE_URL': bcompSettings.seti.baseUrl,
  'OREO_REMOTE_URL': bcompSettings.oreo.baseUrl,
  'DSPC_REMOTE_URL': bcompSettings.dspc.baseUrl,
  'CERN_REMOTE_URL': bcompSettings.cern.baseUrl,
  'WORM_REMOTE_URL': bcompSettings.worm.baseUrl,
});

@Injectable({
  providedIn: 'root'
})
export class WarpService implements OnDestroy {

  private wuiApp: ElementRef;
  private onLanguageChangeSubscription: Subscription;

  constructor(
    private httpClient: HttpClient,
    private oAuthService: OAuthService,
    private translate: TranslateService,
    private clientAccountService: ClientAccountService,
    private customerConverter: CustomerConverter,
    private accountConverter: AccountConverter,
    private configurationService: ConfigurationService) {
  }

  private getBaseResourceEndpoint() {
    return this.configurationService.configuration.api.host
  }

  initWarpFlow(wui, processId, articleTypes?) {
    const myWuiAppProperties = {
      // Properties di STARTUP
      FRONTEND_URL: '${window.location.origin}',
      'wui.feUrl': '${FRONTEND_URL}/ui',
      'wui.feResourcesUrl': '${FRONTEND_URL}/ui-resources',
      'wui.assetsUrl': '${FRONTEND_URL}/warp-assets',

      REMOTE_URL: this.configurationService.configuration.orchestrator.base,
      'wui.apiUrl': '${REMOTE_URL}/warp',
      'wui.processId.queryParamName': 'pid',
      'wui.app-config.loader.enabled': false,
      'wui.app-config.loader.url': '${wui.feUrl}/config.json',

      'wui.bcomp.i18n.use-generated-task-name': true,
      'wui.i18n.languages': this.translate.getLangs(),
      'wui.i18n.language.default':
        this.translate.currentLang || this.translate.defaultLang,
      'wui.i18n.loader.type': 'MLDR',
      'wui.i18n.loader.options': {
        mldr: {
          baseUrl: this.configurationService.configuration.api.translate,
          application: 'WARP',
        },
      },

      // Properties di configurazione del loader di livello Process
      'wui.process-config.loader.enabled': false,
      'wui.process-config.loader.baseUrl': '${wui.feResourcesUrl}',
      'wui.process-config.loader.fromWorkflowId.mandatory': false,
      'wui.process-config.loader.fromQueryParam.denied': false,

      //todo configurare per collaudo e produzione
      // Properties di configurazione dei business-component
      ...bcompRemoteUrlConfigurations(
        this.configurationService.configuration.bcomp
      ),

      'wui.bcomp.gattaca.componentTag': 'gattaca-master-bcomp',
      'wui.bcomp.gattaca.activityApiUrl': '${GATTACA_REMOTE_URL}/gattaca',
      'wui.bcomp.gattaca.activityAssetsUrl': '${FRONTEND_URL}/gattaca/assets',

      'wui.bcomp.seti.componentTag': 'seti-master-bcomp',
      'wui.bcomp.seti.activityApiUrl': '${SETI_REMOTE_URL}/seti',
      'wui.bcomp.seti.activityAssetsUrl': '${FRONTEND_URL}/seti/assets',

      'wui.bcomp.digital-sign-product-configurator.componentTag':
        'digital-sign-product-configurator-master-bcomp',
      'wui.bcomp.digital-sign-product-configurator.activityApiUrl':
        '${DSPC_REMOTE_URL}/digital-sign-product-configurator',
      //No assets existing
      'wui.bcomp.digital-sign-product-configurator.activityAssetsUrl':
        '${FRONTEND_URL}/digital-sign-product-configurator/assets',

      'wui.bcomp.oreo.componentTag': 'oreo-master-bcomp',
      'wui.bcomp.oreo.activityApiUrl': '${OREO_REMOTE_URL}/oreo',
      'wui.bcomp.oreo.activityAssetsUrl': '${FRONTEND_URL}/oreo/assets',

      'wui.bcomp.cern.componentTag': 'cern-bcomp',
      'wui.bcomp.cern.activityApiUrl': '${CERN_REMOTE_URL}/cern',
      'wui.bcomp.cern.activityAssetsUrl': '${FRONTEND_URL}/cern/assets',

      'wui.bcomp.worm.componentTag': 'worm-bcomp',
      'wui.bcomp.worm.activityApiUrl': '${WORM_REMOTE_URL}/worm',
      'wui.bcomp.worm.activityAssetsUrl': '${FRONTEND_URL}/worm/assets',

      'wui.bcomp.chas.componentTag': 'chas-master-bcomp',
      //No assets existing
      'wui.bcomp.chas.activityApiUrl': '${CHAS_REMOTE_URL}/chas',
      'wui.bcomp.chas.activityAssetsUrl': '${FRONTEND_URL}/chas/assets',

      'wui.bcomp.idem.componentTag': 'wb-idem',
      'wui.bcomp.idem.activityApiUrl': '${IDEM_REMOTE_URL}/idem',
      'wui.bcomp.idem.activityAssetsUrl': '${FRONTEND_URL}/idem/assets',

      // diciamo a warp che è iniettato (quindi esterno)mper presentarlo in maniera 'contenuta' senza cornice
      external: true,

      'wui.initial-config.content': {
        activity: [
          {
            match: {
              componentName: 'worm',
            },
            bcomp: {
              i18n: {
                loader: {
                  type: 'MLDR',
                  options: {
                    mldr: {
                      baseUrl:
                        this.configurationService.configuration.api.translate,
                      application: 'WORM',
                    },
                  },
                },
              },
            },
          },
          {
            match: {
              componentName: 'chas',
            },
            bcomp: {
              i18n: {
                loader: {
                  type: 'MLDR',
                  options: {
                    mldr: {
                      baseUrl:
                        this.configurationService.configuration.api.translate,
                      application: 'CHAS',
                    },
                  },
                },
              },
            },
          },
          {
            match: {
              componentName: 'gattaca',
            },
            bcomp: {
              activityData: {
                optionalProvinceForForeignBornUsers: true,
                theme: 'eobi',
              },
              i18n: {
                loader: {
                  type: 'MLDR',
                  options: {
                    mldr: {
                      baseUrl:
                        this.configurationService.configuration.api.translate,
                      application: 'GATTACA',
                    },
                  },
                },
              },
            },
          },
          {
            match: {
              componentName: 'seti',
            },
            bcomp: {
              i18n: {
                loader: {
                  type: 'MLDR',
                  options: {
                    mldr: {
                      baseUrl:
                        this.configurationService.configuration.api.translate,
                      application: 'SETI',
                    },
                  },
                },
              },
            },
          },
          {
            match: {
              componentName: 'digital-sign-product-configurator',
            },
            bcomp: {
              i18n: {
                loader: {
                  type: 'MLDR',
                  options: {
                    mldr: {
                      baseUrl:
                        this.configurationService.configuration.api.translate,
                      application: 'digital-sign-product-configurator',
                    },
                  },
                },
              },
            },
          },
          {
            match: {
              componentName: 'cern',
            },
            bcomp: {
              i18n: {
                loader: {
                  type: 'MLDR',
                  options: {
                    mldr: {
                      baseUrl:
                        this.configurationService.configuration.api.translate,
                      application: 'CERN',
                    },
                  },
                },
              },
            },
          },
          {
            match: {
              componentName: 'oreo',
            },
            bcomp: {
              i18n: {
                loader: {
                  type: 'MLDR',
                  options: {
                    mldr: {
                      baseUrl:
                        this.configurationService.configuration.api.translate,
                      application: 'OREO',
                    },
                  },
                },
              },
            },
          },
          {
            match: {
              componentName: 'idem',
            },
            bcomp: {
              i18n: {
                loader: {
                  type: 'MLDR',
                  options: {
                    mldr: {
                      baseUrl:
                        this.configurationService.configuration.api.translate,
                      application: 'IDEM',
                    },
                  },
                },
              },
            },
          },
          {
            // @see "Configuration | Reference" documentation
            match: {
              taskId: 'processEnd',
            },
            layout: {
              components: {
                header: false,
                'process-heading': false,
                'process-navigation': false,
                'step-heading': false,
                'main-actions': false,
                'main-sidebar-right': false,
                footer: false,
              },
            },
            bcomp: {
              activityData: {
                customer: this.customerConverter.convertToDto(
                  this.clientAccountService.getClient()
                ),
                account: this.accountConverter.convertToDto(
                  this.clientAccountService.getAccount()
                ),
                lang: {
                  current: this.translate.currentLang,
                  languages: this.translate.getLangs(),
                },
                theme: 'eobi',
                articleTypes: articleTypes,
              },
              i18n: {
                loader: {
                  type: 'NOOP',
                },
              },
              uiOptions: {
                landing: true,
                mainLayoutMargin: false,
              },
            },
          },
          {
            // @see "Configuration | Reference" documentation
            match: {
              taskId: 'waitForUser',
            },
            layout: {
              components: {
                header: false,
                'process-heading': false,
                'process-navigation': false,
                'step-heading': false,
                'main-actions': false,
                'main-sidebar-right': false,
                footer: false,
              },
            },
            bcomp: {
              activityData: {
                customer: this.customerConverter.convertToDto(
                  this.clientAccountService.getClient()
                ),
                account: this.accountConverter.convertToDto(
                  this.clientAccountService.getAccount()
                ),
                lang: {
                  current: this.translate.currentLang,
                  languages: this.translate.getLangs(),
                },
                theme: 'eobi',
                articleTypes: articleTypes,
              },
              i18n: {
                loader: {
                  type: 'NOOP',
                },
              },
              uiOptions: {
                landing: true,
                mainLayoutMargin: false,
              },
            },
          },
          {
            // @see "Configuration | Reference" documentation
            match: {},
            layout: {
              components: {
                header: false,
                'process-heading': false,
                'process-navigation': true,
                'step-heading': false,
                'main-actions': true,
                'main-sidebar-right': false,
                footer: false,
              },
            },
            bcomp: {
              activityData: {
                customer: this.customerConverter.convertToDto(
                  this.clientAccountService.getClient()
                ),
                account: this.accountConverter.convertToDto(
                  this.clientAccountService.getAccount()
                ),
                lang: {
                  current: this.translate.currentLang,
                  languages: this.translate.getLangs(),
                },
                theme: 'eobi',
                articleTypes: articleTypes,
              },
              i18n: {
                loader: {
                  type: 'NOOP',
                },
              },
              uiOptions: {
                landing: true,
                mainLayoutMargin: false,
              },
            },
          },
        ],
      },
    };

    const myProcessIdProvider = {
      getProcessId: () => processId          // <- fornire il processId
    };

    const getToken = () => {
      return this.oAuthService.getAccessToken();
    };

    class CustomAuthClient {

      // wui-app lo invoca ogni volta che ha necessità di interrogare api warp autenticate e/o i business-component che coordina invocano le proprie api autenticate
      getAuthorizationHeaderValue() {
        const tokenType = 'bearer';
        const accessToken = getToken();     // <- fornire l'accessToken aggiornato
        return `${tokenType} ${accessToken}`;
      }

      // wui-app lo invoca se il processo corrente richiede autenticazione
      useAuthentication() {
      }

      // check sincrono, considero true in quanto sono un integratore e se arrivo a mostrare warp sono già autenticato
      isLoggedIn() {
        return true;
      }

      // Promise che risolve solo dopo aver eventualmente verificato con il back-end che l'autenticazione dell'app integratore sia valida
      async asyncEnsureAuth() {
        return true;
      }

      // invocato se isLoggedIn() ha restituito false oppure asyncEnsureAuth() ha restituito false
      async startAuthentication(state) {
        return true;
      }

      // invocato se il valore restituito da getAuthorizationHeaderValue() non è valido verso le api warp (api authorized non ha dato esito positivo)
      async startLogout(state) {
        return true;
      }
    }

    const myAuthClient = new CustomAuthClient();

    // NOTA: $wuiApp è la reference al componente <wui-app></wui-app> linkato dal front-end dell'integratore

    // initial config properties
    wui.nativeElement.props = myWuiAppProperties;

    // services
    wui.nativeElement.onBeforeInit = (wuiProvider) => {
      wuiProvider.register('ProcessIdProvider', () => myProcessIdProvider);
      wuiProvider.register('WuiAuthClient', () => myAuthClient);

      this.subscribeLanguageUpdateEvent();
    };

    // startup
    wui.nativeElement._boot();

    this.wuiApp = wui;
  }

  ngOnDestroy(): void {
    this.onLanguageChangeSubscription.unsubscribe();
  }

  private subscribeLanguageUpdateEvent() {
    this.onLanguageChangeSubscription = this.translate.onLangChange.pipe(
      map(e => e.lang),
      debounceTime(10),
      distinctUntilChanged(),
      tap(language => {
        const warpTranslateService: any = this.wuiApp.nativeElement.provider.get('WarpTranslateService');
        warpTranslateService.use(language);
      })
    ).subscribe();
  }

  public createWarpProcess(unitParams: WarpProcessRequest): Observable<string> {
    const url = this.getBaseResourceEndpoint() + 'processes';
    return this.httpClient.post(url, unitParams).pipe(
      map((dtos: any) => {
        return dtos?.processId;
      })
    );
  }

  public buildWarpProcessRequest(
    processType: ProcessType,
    currentLang: string,
    recognitionType: string,
    userId:string,
    recSelected
  ): WarpProcessRequest {
    let wpr: WarpProcessRequest = {
      attributes: [],
      filter: {},
      processType: processType,
    };
    wpr.attributes.push("language:" + currentLang);
    wpr.attributes.push("userId:" + userId);
    wpr.attributes.push(
      "identificationType:" +
        (recognitionType ? recognitionType : recSelected)
    );
    wpr.filter.accountCode =
      this.clientAccountService.getAccount()?.accountCode;
    wpr.filter.customerCode =
      this.clientAccountService.getClient()?.customerCode;
    wpr.filter.management = Management.RESELLER;

    return wpr;
  }

  public getWarpProcesses(productIdParam, processTypeParam, unitCodeParam): Observable<ProcessStartedModel[]> {
    const params = new HttpParams({
      fromObject: {
        productId: productIdParam,
        processType: processTypeParam,
        unitCode: unitCodeParam
      }
    })

    const url = this.getBaseResourceEndpoint() + 'processes';
    return this.httpClient.get<ProcessStartedModel[]>(url, { params: params })
  }
}
